import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@structural-climbing/ui'
import type { InvoiceData } from './types'
import { paymentMethodBadgeClass } from './paymentMethod'

interface InvoiceModalProps {
  orderId: string | null
  onClose: () => void
}

export function InvoiceModal({ orderId, onClose }: InvoiceModalProps) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const printRef = useRef<HTMLDivElement>(null)

  const [data, setData] = useState<InvoiceData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orderId) return
    setData(null)
    setError(null)
    async function load() {
      setLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/invoices/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) {
          setError(t('invoices.errorLoadDetail'))
          return
        }
        setData((await res.json()) as InvoiceData)
      } catch {
        setError(t('invoices.errorLoadDetail'))
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [orderId, apiUrl, getAccessTokenSilently, t])

  function handlePrint() {
    const content = printRef.current
    if (!content || !data) return
    const win = window.open('', '_blank', 'width=900,height=700')
    if (!win) return
    win.document
      .write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${data.invoiceNumber}</title><style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; font-size: 13px; color: #111; padding: 32px; }
      h1 { font-size: 20px; margin-bottom: 4px; }
      .meta { color: #555; font-size: 12px; margin-bottom: 24px; }
      .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 24px; }
      .section-title { font-size: 11px; font-weight: bold; text-transform: uppercase; color: #777; margin-bottom: 8px; letter-spacing: 0.05em; }
      .section-value { font-size: 13px; line-height: 1.6; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
      th { text-align: left; border-bottom: 2px solid #111; padding: 6px 8px; font-size: 12px; }
      td { padding: 6px 8px; border-bottom: 1px solid #ddd; font-size: 13px; }
      .totals { text-align: right; }
      .totals td { border: none; padding: 3px 8px; }
      .totals .grand-total { font-weight: bold; font-size: 15px; border-top: 2px solid #111; }
      @media print { body { padding: 0; } }
    </style></head><body>${content.innerHTML}</body></html>`)
    win.document.close()
    win.focus()
    setTimeout(() => {
      win.print()
    }, 400)
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    return (
      d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
      ' ' +
      d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })
    )
  }

  return (
    <Modal
      isOpen={!!orderId}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader>{t('invoices.modalTitle')}</ModalHeader>
        <ModalBody>
          {loading && (
            <div className="flex justify-center py-8">
              <Spinner size="lg" color="primary" />
            </div>
          )}
          {error && <p className="text-sm text-danger">{error}</p>}
          {data && (
            <div ref={printRef} className="space-y-6 text-sm">
              {/* Header */}
              <div>
                <h1 className="text-xl font-bold">{data.invoiceNumber}</h1>
                <p className="text-xs text-foreground/50">
                  {t('invoices.date')}: {formatDate(data.createdAt)} &nbsp;·&nbsp;
                  {t('invoices.paymentMethod')}:{' '}
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${paymentMethodBadgeClass(
                      data.paymentMethod,
                    )}`}
                  >
                    {t(`invoices.${data.paymentMethod}`)}
                  </span>
                </p>
              </div>

              {/* Supplier + Client */}
              <div className="grid grid-cols-2 gap-6">
                {/* Supplier */}
                <div>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-foreground/40">
                    {t('invoices.supplier')}
                  </p>
                  {data.gym ? (
                    <div className="space-y-0.5 text-sm">
                      <p className="font-semibold">{data.gym.legalName || data.gym.name}</p>
                      {data.gym.legalName && data.gym.legalName !== data.gym.name && (
                        <p className="text-foreground/60">{data.gym.name}</p>
                      )}
                      {data.gym.address && <p className="text-foreground/60">{data.gym.address}</p>}
                      {data.gym.cui && <p className="text-foreground/60">CUI: {data.gym.cui}</p>}
                      {data.gym.regCom && (
                        <p className="text-foreground/60">Reg. Com.: {data.gym.regCom}</p>
                      )}
                      {data.gym.iban && <p className="text-foreground/60">IBAN: {data.gym.iban}</p>}
                      {data.gym.email && <p className="text-foreground/60">{data.gym.email}</p>}
                    </div>
                  ) : (
                    <p className="text-foreground/40">—</p>
                  )}
                </div>

                {/* Client */}
                <div>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-foreground/40">
                    {t('invoices.client')}
                  </p>
                  {data.billing ? (
                    <div className="space-y-0.5 text-sm">
                      {data.billing.billingType === 'individual' ? (
                        <>
                          <p className="font-semibold">
                            {[data.billing.firstName, data.billing.lastName]
                              .filter(Boolean)
                              .join(' ') || '—'}
                          </p>
                          {data.billing.cnp && (
                            <p className="text-foreground/60">CNP: {data.billing.cnp}</p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="font-semibold">{data.billing.companyName || '—'}</p>
                          {data.billing.cui && (
                            <p className="text-foreground/60">CUI: {data.billing.cui}</p>
                          )}
                          {data.billing.regCom && (
                            <p className="text-foreground/60">Reg. Com.: {data.billing.regCom}</p>
                          )}
                        </>
                      )}
                      {data.billing.address && (
                        <p className="text-foreground/60">{data.billing.address}</p>
                      )}
                      {(data.billing.city || data.billing.county) && (
                        <p className="text-foreground/60">
                          {[data.billing.city, data.billing.county].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-foreground/40 italic">{t('invoices.receiptOnly')}</p>
                  )}
                </div>
              </div>

              {/* Items table */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-foreground/20">
                    <th className="py-2 text-left font-semibold">{t('invoices.colDescription')}</th>
                    <th className="py-2 text-right font-semibold">{t('invoices.colQty')}</th>
                    <th className="py-2 text-right font-semibold">{t('invoices.colUnitPrice')}</th>
                    <th className="py-2 text-right font-semibold">{t('invoices.colVat')}</th>
                    <th className="py-2 text-right font-semibold">{t('invoices.colLineTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item, i) => {
                    const lineTotal = item.quantity * item.unitPrice
                    return (
                      <tr key={i} className="border-b border-foreground/10">
                        <td className="py-2">{item.name}</td>
                        <td className="py-2 text-right">{item.quantity}</td>
                        <td className="py-2 text-right">{item.unitPrice.toFixed(2)}</td>
                        <td className="py-2 text-right">{item.vatRate}%</td>
                        <td className="py-2 text-right font-medium">{lineTotal.toFixed(2)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end">
                <table className="w-48 text-sm">
                  <tbody>
                    <tr>
                      <td className="py-1 text-foreground/60">{t('invoices.subtotal')}</td>
                      <td className="py-1 text-right">
                        {(data.totalAmount - data.vatAmount).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1 text-foreground/60">{t('invoices.vat')}</td>
                      <td className="py-1 text-right">{data.vatAmount.toFixed(2)}</td>
                    </tr>
                    <tr className="border-t-2 border-foreground/20">
                      <td className="py-2 font-bold">{t('invoices.total')}</td>
                      <td className="py-2 text-right font-bold text-base">
                        {data.totalAmount.toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onClick={onClose}>
            {t('common.done')}
          </Button>
          {data && (
            <Button color="primary" onClick={handlePrint}>
              {t('invoices.print')}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
